import { Modal } from "PFComponents/modal";
import { Import, ImportBase } from "PFTypes";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { JsonObject } from "type-fest";

import { ImportModalHeader, ImportModalHeaderProps } from "./components/import_modal_header";
import { ImportModalPageType } from "./constants/import_modal_page";
import { useImportModalContext } from "./contexts/import_modal_context";
import { ImportModalContextProvider } from "./contexts/import_modal_context_provider";
import css from "./import_modal.module.scss";

type ImportModalProps = {
  title: string;
  initialPage?: ImportModalPageType;
  csvTemplate?: JsonObject | string;
  contentRenderer: Record<ImportModalPageType, FC>;
  showOKButton?: boolean;
  onClose: VoidFunction;
} & ImportModalHeaderProps;

const ImportModalContent = ({
  title,
  importsCounter,
  pageKey,
  contentRenderer,
  showOKButton,
  onClose
}: Omit<ImportModalProps, "initialPage" | "csvTemplate">) => {
  const { t } = useTranslation("translation");
  const { currentPage, setCurrentPage } = useImportModalContext();

  const isUploadPage = currentPage === ImportModalPageType.Upload;

  const CurrentPageComponent = useMemo(() => contentRenderer[currentPage], [contentRenderer, currentPage]);

  return (
    <Modal
      title={title}
      onClose={onClose}
      labelOK={t("close")}
      onOK={() => {}}
      showOKButton={showOKButton}
      secondaryButtonLabel={t("importFiles.startNewImport")}
      onSecondaryButtonClick={() => setCurrentPage(ImportModalPageType.Upload)}
      showSecondaryButton={!isUploadPage}
      size="xxl"
    >
      <div className={css.modalContent}>
        <ImportModalHeader importsCounter={importsCounter} pageKey={pageKey} />
        <div className={css.mainContent}>{CurrentPageComponent && <CurrentPageComponent />}</div>
      </div>
    </Modal>
  );
};

export const ImportModal = <T extends ImportBase = Import>({
  initialPage,
  csvTemplate,
  ...props
}: ImportModalProps) => (
  <ImportModalContextProvider<T> initialPage={initialPage} csvTemplate={csvTemplate}>
    <ImportModalContent {...props} />
  </ImportModalContextProvider>
);
